import { useState } from 'react';
import {
  Alert,
  Autocomplete,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  FormHelperText,
  Link,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Button,
} from '@mui/material';
import sendNodeCommand from 'store/sendNodeCommand';
import useProjectsAsync from 'store/useProjectsAsync';
import IProject from 'types/IProject';
import { IWorkerSummary } from 'types/INodeAgent';
import { MIN_SOFTWARE_REF_PYTAILOR_VERSION, agentSupportsSoftwareRef, isSoftwareRef } from 'utils/softwareRef';
import {
  DEFAULT_MAX_TASKS_PER_PROCESS,
  MIN_REPLACE_PYTAILOR_VERSION,
  agentSupportsOvershoot,
  agentSupportsReplace,
  defaultReplacementBudget,
  groupMembers,
} from 'utils/workerGroup';
import { errorMessage } from './errorMessage';
import { SlotBudget, fitsSlotBudget, slotBudgetHint } from 'utils/slotBudget';

export type WorkerSource = 'package' | 'depot';

interface StartWorkerDialogProps {
  nodeAgentId: string;
  agentPytailorVersion?: string | null;
  // The node's workers, for the Replace option: a start whose parameters match an
  // existing group can take that group over instead of adding to it. They also name
  // the working directories already in use, which a new worker cannot have.
  workers?: IWorkerSummary[];
  template?: IWorkerSummary;
  // The node's slot budget, when it has one: the slots field is checked against it.
  slotBudget?: SlotBudget | null;
  onClose: () => void;
  onSubmit: () => void;
}

function packageSpecifier(template?: IWorkerSummary): string {
  const name = template?.package_name;
  // No package name means a Depot-based worker, which has no provisioning
  // package at all — its installed_version is not a pin the user chose. Fall
  // back to the same bare default a blank dialog uses.
  if (!name) return 'pytailor';
  if (name.includes('==')) return name;
  const version = template?.package_version || template?.installed_version;
  return version ? `${name}==${version}` : name;
}

export default function StartWorkerDialog({
  nodeAgentId,
  agentPytailorVersion,
  workers = [],
  template,
  slotBudget,
  onClose,
  onSubmit,
}: StartWorkerDialogProps) {
  const depotSupported = agentSupportsSoftwareRef(agentPytailorVersion);
  // A template reproduces what that worker runs: the frozen pin, not the alias it was
  // asked for. Only a worker that never provisioned (START_FAILED) has just the alias.
  const templateSoftwareRef = template?.software ?? template?.software_ref ?? '';
  const [source, setSource] = useState<WorkerSource>(templateSoftwareRef && depotSupported ? 'depot' : 'package');
  const [softwareRef, setSoftwareRef] = useState(templateSoftwareRef);
  const [packageName, setPackageName] = useState(packageSpecifier(template));
  const [slots, setSlots] = useState(template ? String(template.slots) : '1');
  const overshootSupported = agentSupportsOvershoot(agentPytailorVersion);
  const [overshoot, setOvershoot] = useState(String(template?.overshoot ?? 0));
  // Faithful to the template, as the rolling path in UpgradeWorkerDialog is: with Replace
  // ticked these fields are the group's, not a fresh worker's, so a default here would
  // silently re-tune it. A summary from an agent too old to report them falls back.
  const [sleep, setSleep] = useState(template?.sleep != null ? String(template.sleep) : '3');
  const [workerName, setWorkerName] = useState(template?.name ?? '');
  const [workdirName, setWorkdirName] = useState('');
  const [selectedProjects, setSelectedProjects] = useState<IProject[]>([]);
  const [projectsSeeded, setProjectsSeeded] = useState(false);
  const [capabilities, setCapabilities] = useState<string[]>(template?.capabilities ?? []);
  // Track the Autocomplete's input so a capability the user typed but didn't
  // commit with Enter/blur before clicking Start still gets included.
  const [capabilityInput, setCapabilityInput] = useState('');
  const [pythonVersion, setPythonVersion] = useState(template?.python_version || '');
  const [maxTasksPerProcess, setMaxTasksPerProcess] = useState(
    String(template?.max_tasks_per_process ?? DEFAULT_MAX_TASKS_PER_PROCESS),
  );
  const [replace, setReplace] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Every worker gets its own directory, so a name another worker already has is
  // refused by the agent. Say so here rather than let Start earn a failed row.
  const workdirTaken = workers.some((ws) => ws.workdir_name && ws.workdir_name === workdirName.trim());

  const projectsAsync = useProjectsAsync();
  const projects = projectsAsync.status === 'SUCCESS' || projectsAsync.status === 'REFRESH' ? projectsAsync.data : [];

  if (!projectsSeeded && projects.length > 0 && template?.project_id_filter?.length) {
    setProjectsSeeded(true);
    setSelectedProjects(projects.filter((project) => template.project_id_filter!.includes(project.id)));
  }

  const effectiveSoftwareRef = softwareRef.trim();
  const replaceSupported = agentSupportsReplace(agentPytailorVersion);
  const pendingCapability = capabilityInput.trim();
  const replaceable = groupMembers(workers, {
    name: workerName,
    projectIdFilter: selectedProjects.map((p) => p.id),
    capabilities:
      pendingCapability && !capabilities.includes(pendingCapability)
        ? [...capabilities, pendingCapability]
        : capabilities,
    packageName: source === 'package' ? packageName : null,
    softwareRef: source === 'depot' ? effectiveSoftwareRef : null,
  });
  const replaceActive = replace && replaceable.length > 0;
  const requestedSlots = parseInt(slots, 10) || 1;
  const transferredSlots = replaceActive ? defaultReplacementBudget(replaceable) : 0;
  const slotsFit = fitsSlotBudget(slotBudget ?? null, requestedSlots, transferredSlots);
  const slotsHint = slotBudgetHint(slotBudget ?? null, requestedSlots, transferredSlots);
  const softwareRefInvalid = source === 'depot' && effectiveSoftwareRef !== '' && !isSoftwareRef(effectiveSoftwareRef);
  // Same guard as ResizeWorkerDialog: digits that overflow to Infinity serialize as null,
  // which the command would take as no overshoot at all.
  const overshootValid =
    !overshootSupported || (/^\d+$/.test(overshoot.trim()) && Number.isSafeInteger(Number(overshoot.trim())));
  const canStart =
    workerName.trim() !== '' &&
    !isSubmitting &&
    (source === 'package' || (depotSupported && effectiveSoftwareRef !== '' && !softwareRefInvalid)) &&
    slotsFit &&
    overshootValid &&
    !workdirTaken;

  const handleSubmit = async () => {
    if (isSubmitting) return;

    setIsSubmitting(true);
    setError(null);
    const pendingCap = capabilityInput.trim();
    const finalCapabilities =
      pendingCap && !capabilities.includes(pendingCap) ? [...capabilities, pendingCap] : capabilities;
    // 0 is a real value (never recycle) — only fall back to the default on empty/invalid input.
    // Number, not parseInt: the field takes a fraction or a negative that min doesn't refuse,
    // and truncating one into a budget is worse than the default (-0.5 would submit 0 = never recycle).
    const rawMaxTasks = maxTasksPerProcess.trim();
    const parsedMaxTasks = Number(rawMaxTasks);
    const finalMaxTasks =
      rawMaxTasks === '' || !Number.isSafeInteger(parsedMaxTasks) || parsedMaxTasks < 0
        ? DEFAULT_MAX_TASKS_PER_PROCESS
        : parsedMaxTasks;
    try {
      await sendNodeCommand(nodeAgentId, 'start_worker', {
        ...(source === 'depot'
          ? { software_ref: effectiveSoftwareRef }
          : {
              package_name: packageName,
              ...(pythonVersion.trim() && { python_version: pythonVersion.trim() }),
            }),
        slots: parseInt(slots, 10) || 1,
        ...(overshootSupported && parseInt(overshoot, 10) > 0 && { overshoot: parseInt(overshoot, 10) }),
        sleep: parseFloat(sleep) || 3,
        worker_name: workerName,
        ...(workdirName && { workdir_name: workdirName }),
        ...(selectedProjects.length > 0 && {
          project_id_filter: selectedProjects.map((p) => p.id),
        }),
        ...(finalCapabilities.length > 0 && { capabilities: finalCapabilities }),
        max_tasks_per_process: finalMaxTasks,
        ...(replaceActive && { replace: true }),
      });
      onSubmit();
    } catch (err) {
      console.error('Failed to start worker:', err);
      setError(`Failed to start worker: ${await errorMessage(err)}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Block dismiss while a request is in flight — the parent unmounts on
  // close, dropping isSubmitting, which would otherwise allow a duplicate
  // submit on reopen.
  const handleClose = () => {
    if (isSubmitting) return;
    onClose();
  };

  const agentTooOldReason = `Needs a node agent on pytailor ${MIN_SOFTWARE_REF_PYTAILOR_VERSION} or newer (this one runs ${agentPytailorVersion || 'an unknown version'}).`;
  const depotDisabledReason = !depotSupported ? agentTooOldReason : null;

  return (
    <Dialog open onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{template?.name ? `Start Worker from ${template.name}` : 'Start Worker'}</DialogTitle>
      <DialogContent>
        <ToggleButtonGroup
          exclusive
          value={source}
          onChange={(_, value: WorkerSource | null) => {
            if (value) setSource(value);
          }}
          size="small"
          aria-label="Software source"
          sx={{ mt: 1 }}
        >
          <ToggleButton value="package">Gemfury package</ToggleButton>
          <Tooltip title={depotDisabledReason ?? ''}>
            <span>
              <ToggleButton value="depot" disabled={depotDisabledReason !== null}>
                Depot release
              </ToggleButton>
            </span>
          </Tooltip>
        </ToggleButtonGroup>
        <FormHelperText sx={{ mb: 1 }}>
          {source === 'depot'
            ? 'An immutable install tree from Depot: no index traffic at start, cached per host.'
            : 'A package pip-installed from Gemfury into a fresh virtual environment.'}
        </FormHelperText>
        {source === 'depot' ? (
          <TextField
            label="Release ref"
            required
            value={softwareRef}
            onChange={(e) => setSoftwareRef(e.target.value)}
            fullWidth
            margin="normal"
            error={softwareRefInvalid || !depotSupported}
            placeholder="e.g. ago-orcaflex-wfs@0.29.1"
            helperText={
              softwareRefInvalid ? (
                'Expected name@version (a pin) or name:major-alias (e.g. pytailor:0-stable).'
              ) : (
                <>
                  A pin (<code>name@version</code>) or an alias (<code>name:0-stable</code>, resolved once at start).
                  Browse releases at{' '}
                  <Link href="https://depot.entail.io/" target="_blank" rel="noopener">
                    depot.entail.io
                  </Link>
                  .
                </>
              )
            }
          />
        ) : (
          <>
            <TextField
              label="Package Name"
              value={packageName}
              onChange={(e) => setPackageName(e.target.value)}
              fullWidth
              margin="normal"
              helperText="Supports version pinning, e.g. pytailor==0.11.0"
            />
            <TextField
              label="Python Version (optional)"
              value={pythonVersion}
              onChange={(e) => setPythonVersion(e.target.value)}
              fullWidth
              margin="normal"
              placeholder="e.g. 3.11"
              helperText="Leave empty to use the node agent's own Python."
            />
          </>
        )}
        <TextField
          label="Slots"
          type="number"
          value={slots}
          onChange={(e) => setSlots(e.target.value)}
          fullWidth
          margin="normal"
          error={!slotsFit}
          helperText={
            slotsHint
              ? `Maximum tasks this worker will run in parallel. ${slotsHint}`
              : 'Maximum tasks this worker will run in parallel.'
          }
        />
        {overshootSupported && (
          <TextField
            label="Overshoot (FAST)"
            type="number"
            value={overshoot}
            onChange={(e) => setOvershoot(e.target.value)}
            fullWidth
            margin="normal"
            slotProps={{ htmlInput: { min: 0 } }}
            error={!overshootValid}
            helperText={
              overshootValid
                ? 'FAST-lane tasks this worker runs above its slots so interactive work starts while the slots are busy; BATCH and BACKGROUND never use them. Not charged to the node slot budget.'
                : 'Enter a whole number (0 or more).'
            }
          />
        )}
        <TextField
          label="Sleep Interval (seconds)"
          type="number"
          value={sleep}
          onChange={(e) => setSleep(e.target.value)}
          fullWidth
          margin="normal"
          helperText="Seconds the worker waits between check-ins to the server."
        />
        <TextField
          label="Worker Name"
          required
          value={workerName}
          onChange={(e) => setWorkerName(e.target.value)}
          fullWidth
          margin="normal"
          helperText="Identifies this worker. A workflow can be restricted to a worker name so only matching workers pick up its tasks."
        />
        <TextField
          label="Working Directory (optional)"
          value={workdirName}
          onChange={(e) => setWorkdirName(e.target.value)}
          fullWidth
          margin="normal"
          error={workdirTaken}
          helperText={
            workdirTaken
              ? 'Another worker on this node already has this working directory.'
              : 'Leave empty to name it after the worker. Every worker gets its own.'
          }
        />
        <Autocomplete
          multiple
          options={projects}
          getOptionLabel={(option) => option.name}
          value={selectedProjects}
          onChange={(_, value) => setSelectedProjects(value)}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Project Filters (optional)"
              margin="normal"
              helperText="Only pick up tasks from the selected projects. Leave empty to accept any project."
            />
          )}
        />
        <Autocomplete
          multiple
          freeSolo
          options={[]}
          value={capabilities}
          onChange={(_, value) => setCapabilities(value as string[])}
          inputValue={capabilityInput}
          onInputChange={(_, value) => setCapabilityInput(value)}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Capabilities (optional)"
              margin="normal"
              helperText="Tags this worker provides. A task that requires a capability will only run on a worker that has it. Type and press Enter to add."
            />
          )}
        />
        <TextField
          label="Max tasks per process"
          type="number"
          value={maxTasksPerProcess}
          onChange={(e) => setMaxTasksPerProcess(e.target.value)}
          fullWidth
          margin="normal"
          slotProps={{ htmlInput: { min: 0 } }}
          helperText="Tasks a subprocess runs before recycling. 1 = a fresh process per task; higher amortizes startup on light workloads; 0 = never recycle."
        />
        {replaceable.length > 0 && (
          <>
            <Tooltip
              title={
                replaceSupported
                  ? ''
                  : `Needs a node agent on pytailor ${MIN_REPLACE_PYTAILOR_VERSION} or newer (this one runs ${agentPytailorVersion || 'an unknown version'}).`
              }
            >
              <FormControlLabel
                control={
                  <Checkbox
                    checked={replaceActive}
                    disabled={!replaceSupported}
                    onChange={(e) => {
                      setReplace(e.target.checked);
                      if (e.target.checked) setSlots(String(defaultReplacementBudget(replaceable) || 1));
                    }}
                  />
                }
                label="Replace the existing workers in this group (rolling)"
                sx={{ mt: 1, mb: 0 }}
              />
            </Tooltip>
            <FormHelperText sx={{ ml: 4, mt: 0 }}>
              Same name, project filter, capabilities and software as{' '}
              {replaceable
                .map((w) => `${w.instance_id} (${w.status.toLowerCase()}, ${w.running_tasks ?? 0} running)`)
                .join(', ')}
              .{' '}
              {replaceActive
                ? 'The new worker starts with no slots, the existing ones retire, and slots move over as their running tasks finish; Slots is the budget it ends up with.'
                : 'Unchecked, this worker is added beside them.'}
            </FormHelperText>
          </>
        )}
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button onClick={handleSubmit} variant="contained" color="primary" disabled={!canStart}>
          {isSubmitting ? 'Starting…' : replaceActive ? 'Replace' : 'Start'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
